import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { BaseApiService } from '../../shared/base-service/base-api.service';
import { Message } from 'primeng/api';
import { PlateModel } from '../../titleregistration/models/plate.model';

@Injectable({
    providedIn: 'root'
})
export class MotorCarrierSupplementService extends BaseApiService {
    constructor() {
        super();
    }
    // Gets or sets the messages that will be pushed to the p-growl component
    public growlMessages: Message[] = [];

    /**
     * Gets the motor carrier supplement.
     * @param motorCarrierAccountNumberId The motor carrier account number id.
     * @param motorCarrierAccountSupplementId The motor carrier account supplement id.
     */
    public getSupplement(motorCarrierAccountNumberId: number, motorCarrierAccountSupplementId: number) {
        return this.get('IRP/MotorCarrierSupplement/Account/' + motorCarrierAccountNumberId + '/Supplement/' + motorCarrierAccountSupplementId);
    }

    /**
     * Gets the motor carrier supplement summary.
     * @param motorCarrierAccountNumberId The motor carrier account number id.
     * @param motorCarrierAccountSupplementId The motor carrier account supplement id.
     */
    public getMotorCarrierSupplementSummary(motorCarrierAccountNumberId: number, motorCarrierAccountSupplementId: number) {
        return this.get('IRP/MotorCarrierSupplement/Account/' + motorCarrierAccountNumberId + '/Supplement/' + motorCarrierAccountSupplementId + '/Invoice/Summary');
    }

    /**
     * Gets the motor carrier supplement invoice.
     * @param motorCarrierAccountSupplementId The motor carrier account supplement id.
     */
    public getSupplementInvoice(motorCarrierAccountSupplementId: number) {
        return this.get('IRP/MotorCarrierSupplement/' + motorCarrierAccountSupplementId + '/Invoice');
    }

    /**
     * Cancels the motor carrier supplement.
     * @param motorCarrierAccountNumberId The motor carrier account number id.
     * @param motorCarrierAccountSupplementId The motor carrier account supplement id.
     */
    public cancelMotorCarrierSupplement(motorCarrierAccountNumberId: number, motorCarrierAccountSupplementId: number) {
        return this.delete('IRP/MotorCarrierSupplement/Account/' + motorCarrierAccountNumberId + '/Supplement/' + motorCarrierAccountSupplementId + '/Invoice');
    }

    /**
     * deletes the motor carrier supplement.
     * @param motorCarrierAccountNumberId The motor carrier account number id.
     * @param motorCarrierAccountSupplementId The motor carrier account supplement id.
     */
    public deleteMotorCarrierSupplement(motorCarrierAccountNumberId: number, motorCarrierAccountSupplementId: number) {
        return this.delete('IRP/MotorCarrierSupplement/Account/' + motorCarrierAccountNumberId + '/Supplement/' + motorCarrierAccountSupplementId);
    }

    /**
     * Gets the active supplement step
     * @param motorCarrierAccountSupplementId The supplement to get the step for
     */
    public getMotorCarrierActiveSupplementStepType(motorCarrierAccountSupplementId: number) {
        return this.get('IRP/MotorCarrierSupplement/Supplement/' + motorCarrierAccountSupplementId + '/Step');
    }

    /**
     * Gets the active supplement step by the transaction number
     * @param businessTransactionNumberId The transaction to get the step for
     */
    public getMotorCarrierActiveSupplementStepTypeByTransactionId(businessTransactionNumberId: number) {
        return this.get('IRP/MotorCarrierSupplement/Supplement/Transaction/' + businessTransactionNumberId + '/Step');
    }

    /**
     * Resets the supplement step to the very beginning
     * @param motorCarrierAccountSupplementId The supplement to reset the step for
     */
    public resetSupplementStepType = (motorCarrierAccountSupplementId: number) => this.get('IRP/MotorCarrierSupplement/Supplement/' + motorCarrierAccountSupplementId + '/Step/Reset');

    /**
     * Gets the motor carrier supplement vehicles
     * @param businessTransactionNumberId The business transaction number id
     */
    public getSupplementVehicleDetails(businessTransactionNumberId: number, motorCarrierAccountSupplementId: number) {
        return this.get('IRP/MotorCarrierSupplement/Supplement/' + motorCarrierAccountSupplementId + '/BusinessTransaction/' + businessTransactionNumberId + '/Vehicles');
    }

    /**
     * Gets the motor carrier supplement vehicle jurisdiction percentages
     * @param businessTransactionNumberId The business transaction number id
     */
    public getSupplementVehicleJurisdictionDetails(businessTransactionNumberId: number) {
        return this.get('IRP/MotorCarrierSupplement/Supplement/' + businessTransactionNumberId + '/Percentages');
    }

    /**
     * check for any supplements which are not completed
     * @param motorCarrierAccountNumberId
     * @param motorCarrierAccountNumberId fleetNumber
     */
    public hasMotorCarrierAccountOpenSupplements(motorCarrierAccountNumberId: number, fleetNumber: number) {
        return this.get('IRP/MotorCarrierSupplement/Account/' + motorCarrierAccountNumberId + '/Fleet/ ' + fleetNumber + '/HasOpenSupplements');
    }

    public voidMotorCarrierSupplement(motorCarrierAccountNumberId: number, motorCarrierAccountSupplementId: number) {
        return this.get('IRP/MotorCarrierSupplement/Account/' + motorCarrierAccountNumberId + '/Supplement/' + motorCarrierAccountSupplementId + '/Void');
    }

    /**
     * Gets the supplement decals
     * @param motorCarrierAccountNumberId
     * @param motorCarrierAccountSupplementId
     */
    public getSupplementDecalSummary(motorCarrierAccountNumberId, motorCarrierAccountSupplementId: number) {
        return this.get(`IRP/MotorCarrierIFTA/${motorCarrierAccountNumberId}/Supplement/${motorCarrierAccountSupplementId}/Decals`);
    }

    /**
     * Gets the fleet renewal supplement vehicle details for display
     * @param fleetId The fleet id being renewed.
     * @param motorCarrierAccountNumberId The supplement motor carrier account number id.
     * @returns A list of fleet vehicle details.
     */
    public getFleetRenewalSupplementVehicles(fleetId: number, motorCarrierAccountNumberId: number) {
        return this.get(`IRP/MotorCarrierSupplement/Supplement/MotorCarrierAccountNumber/${motorCarrierAccountNumberId}/Renewal/Fleet/${fleetId}/vehicles`);
    }

    /**
     * Gets the vehicle display details for an array of vehicle ids.
     * @param vehicleIds The vehicle Ids to get the display details for
     * @param motorCarrierAccountNumberId The supplement motor carrier account number id.
     * @returns An array of vehicle display details
     */
    public getSupplementVehicleDisplayDetails(vehicleIds: number[], motorCarrierAccountNumberId: number) {
        return this.post(`IRP/MotorCarrierSupplement/Supplement/MotorCarrierAccountNumber/${motorCarrierAccountNumberId}/Vehicles`, vehicleIds);
    }

    /**
     * Gets the original supplement for the Error Correction Delete Vehicle
     * @param motorCarrierAccountFleetId The motor carrier account fleet id.
     * @param vehicleId The vehicle id.
     * @returns The supplement id
     */
    public getMotorCarrierErrorCorrectionDeleteOriginalSupplement(motorCarrierAccountFleetId: number, vehicleId: number) {
        return this.get(`IRP/MotorCarrierSupplement/Supplement/${motorCarrierAccountFleetId}/${vehicleId}/ErrorCorrectionDelete`);
    }

    /**
     * Deletes a vehicle from a transaction
     * @param businessTransactionNumberId Business transaction the vehicle in
     * @param vehicleId Vehicle to delete
     * @returns True
     */
    public deleteVehicleFromTransaction(businessTransactionNumberId: number, vehicleId: number) {
        return this.delete('IRP/MotorCarrierSupplement/Transaction/' + businessTransactionNumberId + '/Vehicle/' + vehicleId);
    }

    /**
     * Assigns a plate to the vehicle in the transaction
     * @param plate Plate to assign and reserve
     * @param businessTransactionNumberId Transaction the vehicle is on
     * @param vehicleId Vehicle to assign the plate to
     * @returns List of all validation messages
     */
    public assignPlate(plate: PlateModel, businessTransactionNumberId: number, vehicleId: number) {
        return this.post(`IRP/MotorCarrierSupplement/Transaction/${businessTransactionNumberId}/Vehicle/${vehicleId}/Plate`, plate);
    }
}
